import React from 'react';
import { 
  SparklesIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ArrowTrendingUpIcon 
} from '@heroicons/react/24/outline';
import { useCreditCalculator, formatCurrency, formatSavings } from '../../hooks/useCreditCalculator';

const VariableCreditPurchase = ({ onPricingChange, disabled = false, paymentButton }) => {
  const {
    quantity,
    setQuantity,
    pricingDetails,
    validation,
    selectQuickAmount,
    getNextTierSuggestion,
    config
  } = useCreditCalculator();

  // Notify parent component of pricing changes
  React.useEffect(() => {
    if (onPricingChange) {
      onPricingChange({
        quantity: pricingDetails.quantity,
        totalPrice: pricingDetails.totalPrice,
        pricePerCredit: pricingDetails.pricePerCredit,
        isValid: validation.canProceed
      });
    }
  }, [pricingDetails, validation.canProceed, onPricingChange]);

  const nextTierSuggestion = getNextTierSuggestion();

  return (
    <div className="space-y-4">
      {/* Main Content - Two Column Layout on Large Screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Quantity Controls */}
        <div className="space-y-4">
        {/* Slider */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">Credits</label>
            <div className="text-lg font-bold text-gray-900">{quantity} credits</div>
          </div>
          
          <div className="relative">
            <input
              type="range"
              min={config.MINIMUM_PURCHASE}
              max={config.MAXIMUM_PURCHASE}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              disabled={disabled}
              className={`
                w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                slider
              `}
              style={{
                background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${((quantity - config.MINIMUM_PURCHASE) / (config.MAXIMUM_PURCHASE - config.MINIMUM_PURCHASE)) * 100}%, #E5E7EB ${((quantity - config.MINIMUM_PURCHASE) / (config.MAXIMUM_PURCHASE - config.MINIMUM_PURCHASE)) * 100}%, #E5E7EB 100%)`
              }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>{config.MINIMUM_PURCHASE}</span>
              <span>{config.MAXIMUM_PURCHASE}</span>
            </div>
          </div>
        </div>

        {/* Manual Input */}
        <div className="flex items-center space-x-3">
          <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Exact amount:</label>
          <input
            type="number"
            min={config.MINIMUM_PURCHASE}
            max={config.MAXIMUM_PURCHASE}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            disabled={disabled}
            className={`
              flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm
              focus:ring-blue-500 focus:border-blue-500
              ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
              ${validation.errors.length > 0 ? 'border-red-300 bg-red-50' : ''}
            `}
            placeholder={`Min: ${config.MINIMUM_PURCHASE}`}
          />
          <span className="text-sm text-gray-500">credits</span>
        </div>

        {/* Quick Select Buttons */}
        <div className="space-y-2">
          <div className="text-sm font-medium text-gray-700">Quick select:</div>
          <div className="flex flex-wrap gap-2">
            {config.QUICK_SELECT_OPTIONS.map((amount) => (
              <button
                key={amount}
                onClick={() => selectQuickAmount(amount)}
                disabled={disabled}
                className={`
                  px-3 py-1.5 text-sm font-medium rounded-md border transition-colors
                  ${quantity === amount 
                    ? 'bg-blue-100 border-blue-300 text-blue-700' 
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {amount}
              </button>
            ))}
          </div>
        </div>

        {/* Payment Button */}
        {paymentButton && (
          <div className="mt-4">
            {paymentButton}
          </div>
        )}
        </div>

        {/* Right Column - Pricing Display */}
        <div className="space-y-4">
          {/* Pricing Display */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Price per credit:</span>
          <span className="font-semibold text-gray-900">
            {formatCurrency(pricingDetails.pricePerCredit)}
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Total credits:</span>
          <span className="font-semibold text-gray-900">{pricingDetails.quantity}</span>
        </div>

        {pricingDetails.savings > 0 && (
          <div className="flex items-center justify-between text-green-600">
            <div className="flex items-center">
              <SparklesIcon className="h-4 w-4 mr-1" />
              <span className="text-sm">You save:</span>
            </div>
            <span className="font-semibold">
              {formatSavings(pricingDetails.savings, pricingDetails.savingsPercentage)}
            </span>
          </div>
        )}

        <div className="border-t pt-3 flex items-center justify-between">
          <span className="text-base font-semibold text-gray-900">Total:</span>
          <span className="text-2xl font-bold text-green-600">
            {formatCurrency(pricingDetails.totalPrice)}
          </span>
        </div>

        {/* Tier Badge */}
        <div className="flex justify-center">
          <span className={`
            inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
            ${pricingDetails.tier === 'Standard' ? 'bg-gray-100 text-gray-800' : ''}
            ${pricingDetails.tier === 'Bulk' ? 'bg-blue-100 text-blue-800' : ''}
            ${pricingDetails.tier === 'Premium' ? 'bg-purple-100 text-purple-800' : ''}
            ${pricingDetails.tier === 'Super Premium' ? 'bg-gradient-to-r from-yellow-100 to-orange-100 text-orange-800 border border-orange-200' : ''}
          `}>
            {pricingDetails.tier} Tier
            {pricingDetails.savingsPercentage > 0 && (
              <span className="ml-1">• {pricingDetails.savingsPercentage}% off</span>
            )}
          </span>
        </div>
      </div>

      {/* Validation Messages */}
      {validation.errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <div className="text-sm text-red-800">
                {validation.errors.map((error, index) => (
                  <div key={index}>{error}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Warnings */}
      {validation.warnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
          <div className="flex">
            <InformationCircleIcon className="h-5 w-5 text-yellow-400 flex-shrink-0" />
            <div className="ml-3">
              <div className="text-sm text-yellow-800">
                {validation.warnings.map((warning, index) => (
                  <div key={index}>{warning}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Next Tier Suggestion */}
      {nextTierSuggestion && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start">
            <ArrowTrendingUpIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="ml-3">
              <div className="text-sm text-blue-800">
                <strong>Better value available!</strong>
              </div>
              <div className="text-sm text-blue-700 mt-1">
                Get {nextTierSuggestion.credits} credits for {nextTierSuggestion.tier} pricing - {nextTierSuggestion.savings}
              </div>
              <button
                onClick={() => selectQuickAmount(nextTierSuggestion.credits)}
                disabled={disabled}
                className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-500 disabled:opacity-50"
              >
                Select {nextTierSuggestion.credits} credits →
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>

      {/* Features Reminder */}
      <div className="text-center text-xs text-gray-500 space-y-1">
        <div>✓ Credits never expire</div>
        <div>✓ Secure payment with Paystack</div>
        <div>✓ Instant credit allocation</div>
      </div>
    </div>
  );
};

export default VariableCreditPurchase;